import { useState, useEffect } from 'react';
import AddTeamLeadModal from '../modals/AddTeamLeadModal';
import EditTeamLeadModal from '../modals/EditTeamLeadModal';
import InviteAllModal from '../modals/InviteAllModal';
import { getTeamLeads, upsertTeamLead, deleteTeamLead } from '../../utils/api';
import { ADMIN_EMAILS } from '../../config';
import { useAuth } from '../../hooks/useAuth';

export default function AdminAccess({ showToast }) {
  const { refreshUser } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [inviteAllOpen, setInviteAllOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [confirmEmail, setConfirmEmail] = useState(null);

  function loadLeads() {
    setLoading(true);
    getTeamLeads()
      .then(data => {
        const remote = data || [];
        // Use the real DB record for admin if it exists, so saved teams are reflected
        const adminRecord = remote.find(l => ADMIN_EMAILS.includes(l.email));
        const adminEntry = {
          email: ADMIN_EMAILS[0],
          name: adminRecord?.name || 'Rebecca Rice',
          role: 'Admin',
          teams: adminRecord?.teams ?? '',
          status: 'active',
        };
        const others = remote.filter(l => !ADMIN_EMAILS.includes(l.email));
        setLeads([adminEntry, ...others]);
      })
      .catch(() => {
        setLeads([{
          email: ADMIN_EMAILS[0],
          name: 'Rebecca Rice',
          role: 'Admin',
          teams: '',
          status: 'active',
        }]);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadLeads(); }, []);

  async function handleAddLead(newLead) {
    try {
      await upsertTeamLead(newLead);
      showToast('Team lead added.');
      loadLeads();
    } catch (err) {
      showToast('Error saving team lead.');
      console.error(err);
    }
  }

  async function handleEditLead(updated) {
    const prev = leads;
    setLeads(list => list.map(l => l.email === updated.email ? { ...l, ...updated } : l));
    setEditOpen(false);
    try {
      await upsertTeamLead(updated);
      showToast('Changes saved.');
      // If admin's own record changed, refresh the session so TL View updates immediately
      if (ADMIN_EMAILS.includes(updated.email)) {
        await refreshUser();
      }
    } catch (err) {
      setLeads(prev);
      setEditOpen(true);
      showToast('Error saving changes.');
      console.error(err);
    }
  }

  async function handleDeleteLead(email) {
    const prev = leads;
    setLeads(list => list.filter(l => l.email !== email));
    try {
      await deleteTeamLead(email);
      showToast('Team lead removed.');
    } catch (err) {
      setLeads(prev);
      showToast('Error removing team lead.');
      console.error(err);
    }
  }

  function openEdit(lead) {
    setEditLead(lead);
    setEditOpen(true);
  }

  const displayRole = (lead) => {
    if (ADMIN_EMAILS.includes(lead.email)) return 'Admin';
    return lead.role || 'Team Lead';
  };

  const displayTeams = (lead) => {
    if (ADMIN_EMAILS.includes(lead.email)) return lead.teams || 'All Teams';
    return lead.teams || 'None';
  };

  return (
    <>
      <div className="page-content">
        <div className="page-title-row">
          <div>
            <div className="page-title">Team Lead Access</div>
            <div className="page-sub">Manage who can log in and what they see</div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-sm" onClick={() => setInviteAllOpen(true)}>Invite All</button>
            <button className="btn btn-dark" onClick={() => setAddOpen(true)}>+ Add Team Lead</button>
          </div>
        </div>

        <div className="panel">
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>Loading access list…</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Google Account</th>
                  <th>Team(s)</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {leads.map(lead => (
                  <tr key={lead.email}>
                    <td><span className="td-name">{lead.name || lead.email}</span></td>
                    <td>{lead.email}</td>
                    <td>{displayTeams(lead)}</td>
                    <td>
                      {displayRole(lead) === 'Admin'
                        ? <span className="pill pill-dark">Admin</span>
                        : <span className="pill pill-gray">Team Lead</span>
                      }
                    </td>
                    <td>
                      {lead.status === 'active'      && <><span className="status-dot dot-green" />Active</>}
                      {lead.status === 'pending'     && <><span className="status-dot dot-amber" />Invite Pending</>}
                      {lead.status === 'not-invited' && <><span className="status-dot dot-gray" />Not Invited</>}
                      {lead.status === 'inactive'    && <><span className="status-dot dot-gray" />Inactive</>}
                      {!lead.status                  && <><span className="status-dot dot-green" />Active</>}
                    </td>
                    <td>
                      {confirmEmail === lead.email ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                          <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 500, color: 'var(--black)' }}>Remove {lead.name || lead.email}?</span>
                          <button className="btn btn-xs" onClick={() => setConfirmEmail(null)}>Cancel</button>
                          <button className="btn btn-xs btn-danger" onClick={() => { handleDeleteLead(lead.email); setConfirmEmail(null); }}>Remove</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-sm" onClick={() => openEdit(lead)}>Edit</button>
                          {!ADMIN_EMAILS.includes(lead.email) && (
                            <button className="btn btn-sm btn-danger" onClick={() => setConfirmEmail(lead.email)}>Remove</button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <AddTeamLeadModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSave={handleAddLead}
        showToast={showToast}
      />
      <EditTeamLeadModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        lead={editLead}
        onSave={handleEditLead}
        showToast={showToast}
      />
      <InviteAllModal open={inviteAllOpen} onClose={() => setInviteAllOpen(false)} showToast={showToast} />
    </>
  );
}
